import type { CartCreateDto } from "../../shared/CartCreateDto";
import type { CartDto } from "../../shared/CartDto";
import type { CartPartnerDto } from "../../shared/CartPartnerDto";
import type { CartPatchDto } from "../../shared/CartPatchDto";
import type { CartSpecialPriceDto } from "../../shared/CartSpecialPriceDto";
import type { CartSubmitDto } from "../../shared/CartSubmitDto";
import type { PartyAddressDto } from "../../shared/PartyAddressDto";
import type { ICartReferenceProvider } from "../interfaces/ICartReferenceProvider";
import type { ICartRepository } from "../interfaces/ICartRepository";

export class CartService {
  readonly repo: ICartRepository;
  readonly referenceProvider: ICartReferenceProvider;
  constructor(
    repo: ICartRepository,
    referenceProvider: ICartReferenceProvider,
  ) {
    this.repo = repo;
    this.referenceProvider = referenceProvider;
  }

  async updateCart(patchDto: CartPatchDto): Promise<CartDto | null> {
    const reference = await this.referenceProvider.getReference();
    if (reference == null) {
      return null;
    }
    return await this.repo.update(reference, patchDto);
  }
  async submitCart(subm: CartSubmitDto): Promise<CartDto | null> {
    const reference = await this.referenceProvider.getReference();
    if (reference == null) {
      return null;
    }
    return await this.repo.submitOrder(reference, subm.agbAccepted);
  }

  async setCustomerAddress(dto: PartyAddressDto): Promise<CartDto | null> {
    const reference = await this.referenceProvider.getReference();
    if (reference == null) {
      return null;
    }
    return await this.repo.setCustomerAddress(reference, dto);
  }

  async setPartnerAddress(dto: PartyAddressDto): Promise<CartDto | null> {
    const reference = await this.referenceProvider.getReference();
    if (reference == null) {
      return null;
    }
    return await this.repo.setPartnerAddress(reference, dto);
  }

  async getCart(): Promise<CartDto | null> {
    const reference = await this.referenceProvider.getReference();
    if (reference == null) {
      return null;
    }
    return await this.repo.getByReference(reference);
  }

  async createCart(
    courseId: number,
    isPairBooking: boolean,
  ): Promise<CartDto | null> {
    const dto = {
      courseId: courseId,
      isPairBooking: isPairBooking,
    } satisfies CartCreateDto;
    const newCart = await this.repo.create(dto);
    if (newCart != null) {
      this.referenceProvider.saveReference(newCart.reference);
    }
    return newCart;
  }

  async getCustomerSpecialPrices(): Promise<CartSpecialPriceDto[]> {
    const reference = await this.referenceProvider.getReference();
    if (reference == null) {
      return [];
    }
    return await this.repo.getCustomerSpecialPrices(reference);
  }

  async getPartnerSpecialPrices(): Promise<CartSpecialPriceDto[]> {
    const reference = await this.referenceProvider.getReference();
    if (reference == null) {
      return [];
    }
    return await this.repo.getPartnerSpecialPrices(reference);
  }

  async getPartners(): Promise<CartPartnerDto[]>{
    const reference = await this.referenceProvider.getReference();
    if (reference == null) {
      return [];
    }
    return await this.repo.getPartners(reference);
  }

  async clearCart(): Promise<void> {
    await this.referenceProvider.clearReference();
  }
}
